import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  Clock,
  CheckCircle2,
  ArrowRight,
  ShoppingCart,
} from "lucide-react";

/**
 * Per-request Concierge landing page.
 *
 * Shown to every host who is NOT on a legacy monthly subscription.
 * The page shows standard per-request pricing.
 *
 * The page intentionally has no Stripe CTAs — there is no monthly
 * subscription to start, and per-request activation happens from the
 * specific service request screen.
 */
function formatPriceCents(cents) {
  if (!Number.isFinite(cents) || cents <= 0) return "$25";
  return `$${(cents / 100).toFixed(0)}`;
}

function HeroPerRequest({ priceLabel }) {
  return (
    <div className="rounded-[24px] bg-white border border-[#EAEAEA] p-8 sm:p-10">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-1.5 h-1.5 rounded-full bg-[#EF4949]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#888888]">
          Bellhopt Concierge &middot; Per-request pricing
        </span>
      </div>
      <h1
        className="text-[34px] sm:text-[42px] leading-[1.1] font-extrabold text-[#111111]"
        style={{ letterSpacing: -0.8 }}
      >
        {priceLabel} per fulfilled
        <br className="hidden sm:block" />
        guest concierge request.
      </h1>
      <p className="mt-4 text-[15px] leading-[1.6] text-[#374151] max-w-2xl">
        You only pay when a guest actually submits a concierge request and you
        activate it. No monthly subscription, no per-property fees, no commitment.
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Link
          to="/service-requests"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[12px] bg-[#111111] text-white text-[13px] font-bold hover:bg-black transition-colors"
        >
          See your requests <ArrowRight size={14} />
        </Link>
        <span className="text-[12px] text-[#5F6368]">
          We never charge until you approve activation.
        </span>
      </div>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    {
      icon: <Mail size={14} className="text-[#EF4949]" />,
      title: "Guest submits a request",
      desc:
        "A guest at your property submits Concierge — chef, transfer, spa, mid-stay, anything.",
    },
    {
      icon: <Clock size={14} className="text-[#EF4949]" />,
      title: "You see it in your portal",
      desc:
        "Every request appears under Service Requests with full guest details and notes.",
    },
    {
      icon: <CheckCircle2 size={14} className="text-[#10B981]" />,
      title: "Activate to hand it to Bellhopt",
      desc:
        "Tap activate and Bellhopt handles vendor coordination, payments, and guest follow-up end to end.",
    },
  ];

  return (
    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
      {steps.map((step, idx) => (
        <motion.div
          key={step.title}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.05 }}
          className="rounded-[18px] bg-white border border-[#EAEAEA] p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-[10px] bg-[#FAFAFA] border border-[#EAEAEA] flex items-center justify-center">
              {step.icon}
            </div>
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#888888]">
              Step {idx + 1}
            </span>
          </div>
          <h3 className="text-[15px] font-bold text-[#111111] leading-snug">
            {step.title}
          </h3>
          <p className="mt-1 text-[12.5px] text-[#5F6368] leading-relaxed">
            {step.desc}
          </p>
        </motion.div>
      ))}
    </div>
  );
}

function IncludedFree() {
  return (
    <div className="mt-6 rounded-[18px] bg-[#FAFAFA] border border-[#EAEAEA] p-4 flex items-center gap-3">
      <div className="w-9 h-9 rounded-[10px] bg-white border border-[#EAEAEA] flex items-center justify-center flex-shrink-0">
        <ShoppingCart size={15} className="text-[#10B981]" />
      </div>
      <div>
        <p className="text-[13px] font-bold text-[#111111]">
          Pre-arrival grocery delivery is included free
        </p>
        <p className="text-[11.5px] text-[#5F6368]">
          Active on all your properties at no cost — never counted as a
          concierge request.
        </p>
      </div>
    </div>
  );
}

function FAQ({ priceLabel }) {
  const items = [
    {
      q: "When am I charged?",
      a:
        "Only when a guest submits a concierge request AND you activate it from the request detail screen. No monthly bill, no auto-charges.",
    },
    {
      q: "What's included in the " + priceLabel + " fee?",
      a:
        "Bellhopt takes over the request end-to-end: vendor coordination, payments, guest follow-up, and status updates back to you.",
    },
    {
      q: "Does this affect my early check-in pricing?",
      a:
        "No. Early check-in is your offering at your price. Bellhopt only takes its standard 10% platform fee on early check-in payments.",
    },
  ];

  return (
    <div className="mt-8">
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#888888] mb-3">
        Frequently asked
      </p>
      <div className="space-y-2">
        {items.map((item) => (
          <details
            key={item.q}
            className="group rounded-[14px] border border-[#EAEAEA] bg-white open:bg-[#FAFAFA]"
          >
            <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between text-[13.5px] font-semibold text-[#111111]">
              {item.q}
              <span className="text-[#888888] text-[18px] leading-none group-open:rotate-45 transition-transform">
                +
              </span>
            </summary>
            <div className="px-4 pb-4 text-[12.5px] text-[#5F6368] leading-relaxed">
              {item.a}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

export default function ConciergePerRequestLanding({
  oneTimeUnlockCents,
  // eslint-disable-next-line no-unused-vars
  hostLogo,
}) {
  const priceLabel = formatPriceCents(oneTimeUnlockCents);

  return (
    <div
      className="min-h-screen bg-[#FAFAFA]"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div className="max-w-[1100px] mx-auto px-6 pt-8 pb-16">
        <HeroPerRequest priceLabel={priceLabel} />
        <HowItWorks />
        <IncludedFree />
        <FAQ priceLabel={priceLabel} />
      </div>
    </div>
  );
}
